import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from '../librarianSwal';
import { useActiveLibraryId, useSectionBase } from '../../Constant/useActiveLibrary';
import Loader from '../../Layout/Loader';
import useMinLoading from '../../Layout/useMinLoading';

import {
  getBookCategoriesByLibrary,
  deleteBookCategory,
  updateBookCategoryStatus
} from '../../api/api';

import '../BookList.css';
import '../lib-page-header.css';
import Pagination from '../components/Pagination';

const PAGE_SIZE = 10;

const getErrorMessage = (error) => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    'Something went wrong'
  );
};

const BookCategoryList = () => {
  const navigate = useNavigate();
  const sectionBase = useSectionBase();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const showLoader = useMinLoading(loading);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const libraryId = useActiveLibraryId();

  const fetchCategories = async () => {
    try {
      if (!libraryId) return;

      setLoading(true);
      const data = await getBookCategoriesByLibrary(libraryId);
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Fetch categories error:', error);
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId]);

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: 'Are you sure?',
      text: 'This category will be deleted if no books are linked.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel'
    });

    if (!confirm.isConfirmed) return;

    try {
      await deleteBookCategory(id);

      Swal.fire({
        icon: 'success',
        title: 'Deleted',
        text: 'Category deleted successfully'
      });

      fetchCategories();
    } catch (error) {
      console.error('Delete category error:', error);
      Swal.fire('Error', getErrorMessage(error), 'error');
    }
  };

  const handleStatusChange = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';

    try {
      await updateBookCategoryStatus(id, newStatus);

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: `Category marked as ${newStatus}`
      });

      fetchCategories();
    } catch (error) {
      console.error('Update category status error:', error);
      Swal.fire('Error', getErrorMessage(error), 'error');
    }
  };

  const filteredCategories = categories.filter((item) => {
    const keyword = search.toLowerCase();

    return (
      String(item.name || '').toLowerCase().includes(keyword) ||
      String(item.description || '').toLowerCase().includes(keyword) ||
      String(item.status || '').toLowerCase().includes(keyword)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredCategories.length / PAGE_SIZE));
  const paginatedCategories = filteredCategories.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const getBadgeClass = (status) => {
    if (status === 'active') return 'book-badge-success';
    if (status === 'inactive') return 'book-badge-secondary';
    return 'book-badge-light';
  };

  return (
    <div className="book-list-page">
      <div className="lib-page-header">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon">
            <i className="fas fa-tags" />
          </div>
          <div>
            <h3 className="lib-page-header__title">Book Categories</h3>
            <div className="lib-page-header__subtitle">Manage book type / category master</div>
          </div>
        </div>
        <div className="lib-page-header__actions">
          <button
            type="button"
            className="book-add-btn"
            onClick={() => navigate(`${sectionBase}/book-categories/add`)}
          >
            + Add Category
          </button>
        </div>
      </div>

      <div className="book-filter-card">
        <div className="book-filter-row">
          <input
            type="text"
            className="book-search-input"
            placeholder="Search category..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <button
            type="button"
            className="book-reset-btn"
            onClick={() => setSearch('')}
          >
            Reset
          </button>
        </div>
      </div>

      <div className="book-table-card">
        {showLoader ? (
          <Loader />
        ) : (
          <div className="book-table-wrapper">
            <table className="book-table category-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Category Name</th>
                  <th>Description</th>
                  <th>Total Books</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {paginatedCategories.length > 0 ? (
                  paginatedCategories.map((category, index) => (
                    <tr key={category.id}>
                      <td>{(page - 1) * PAGE_SIZE + index + 1}</td>

                      <td>
                        <div className="book-title">
                          {category.name || '-'}
                        </div>
                      </td>

                      <td>{category.description || '-'}</td>

                      <td>
                        <span className="book-count book-count-blue">
                          {category.total_books || 0}
                        </span>
                      </td>

                      <td>
                        <span className={`book-status ${getBadgeClass(category.status)}`}>
                          {category.status || '-'}
                        </span>
                      </td>

                      <td>
                        <div className="book-action-buttons">
                          <button
                            type="button"
                            className="book-action-btn book-btn-primary bg-blue"
                            onClick={() =>
                              navigate(`${sectionBase}/book-categories/${category.id}/edit`)
                            }
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            className="book-action-btn book-btn-warning bg-yellow"
                            onClick={() =>
                              handleStatusChange(category.id, category.status)
                            }
                          >
                            {category.status === 'active' ? 'Inactive' : 'Active'}
                          </button>

                          <button
                            type="button"
                            className="book-action-btn book-btn-danger bg-red"
                            onClick={() => handleDelete(category.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="book-empty">
                      No categories found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <Pagination
              page={page}
              totalPages={totalPages}
              totalItems={filteredCategories.length}
              itemLabel="categories"
              onPageChange={setPage}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default BookCategoryList;